import type { GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { toast } from 'react-toastify';
import { Auth, withSSRContext } from 'aws-amplify';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';

import styles from './index.module.scss';
import AccountImage from '../../components/AccountImage';
import AccountCreate from '../../components/AccountCreate';
import AccountVerify from '../../components/AccountVerify';

const AccountNavBar = dynamic(() => import('../../components/AccountNavBar'), { ssr: false });

type RegisterProps = {
  PROMO_CODE?: string;
};

const Register = ({ PROMO_CODE }: RegisterProps) => {
  const [email, setEmail] = useState<string>('');
  const [showVerification, setShowVerification] = useState<boolean>(false);
  const [showAgeRestriction, setShowAgeRestriction] = useState<boolean>(false);
  const { query, push } = useRouter();
  const { t } = useTranslation('register');

  useEffect(() => {
    if (showVerification) resendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (query?.context === 'verify' && !!query?.email?.length) {
      setShowVerification(true);

      if (typeof query?.email === 'string') {
        setEmail(query?.email || '');
      }
    }
  }, [query?.context, query?.email]);

  const promoCodeURL = PROMO_CODE ? `?PROMO_CODE=${PROMO_CODE}` : '';

  const resendCode = async () => {
    try {
      await Auth.resendSignUp(email).catch((err) => {
        if (String(err).toLowerCase().includes('already confirmed')) {
          toast.error(t('REGISTER_ALEADY_CONFIRMED_ERROR_MESSAGE'));

          push('/profile');
        }

        throw new Error(err);
      });

      toast.success(t('REGISTER_SUCCESS_MESSAGE'));
    } catch (error: unknown) {
      console.error(error);
      toast.error(t('REGISTER_PAREGISTER_API_ERROR_MESSAGEGE_TITLE'));
    }
  };

  return (
    <div>
      <Head>
        <title>{t('REGISTER_PAGE_TITLE')}</title>
        <meta key="description" name="description" content={t('REGISTER_PAGE_DESCRIPTION')} />
        <link rel="icon" href="/hundo-favicon.png" />
        <meta key="og-title" property="og:title" content={t('REGISTER_PAGE_TITLE')} />
        <meta key="og-description" property="og:description" content={t('REGISTER_PAGE_DESCRIPTION')} />
        <meta key="og-url" property="og:url" content="https://hundo.xyz/register" />
        <meta
          property="og:image"
          content="https://uploads-ssl.webflow.com/60a388b4768e9223fb70ca2b/635c7a25f51427ab9379c247_Social-link-share-CAREERCON22.webp"
        />
        <meta property="og:type" content="website" />
        <meta key="twitter-title" name="twitter:title" content={t('REGISTER_PAGE_TITLE')} />
        <meta key="twitter-description" name="twitter:description" content={t('REGISTER_PAGE_DESCRIPTION')} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@hundo.xyz" />
        <meta
          name="twitter:image"
          content="https://uploads-ssl.webflow.com/60a388b4768e9223fb70ca2b/635c7a25f51427ab9379c247_Social-link-share-CAREERCON22.webp"
        />
      </Head>

      <AccountNavBar promoCode={PROMO_CODE} />
      <main className={styles.container}>
        <div className={styles['inner-container']}>
          <div className={styles.authentication}>
            {!showVerification && !showAgeRestriction && (
              <>
                <p className={styles.step}>{t('REGISTER_PAGE_STEP_HEADER', { step: 1 })}</p>
                <h1>{t('REGISTER_PAGE_DEFAULT_HEADER')}</h1>
                <p className={styles['sub-title']}>{t('REGISTER_PAGE_DEFAULT_SUB_HEADER')}</p>

                <AccountCreate
                  email={email}
                  setEmail={setEmail}
                  setShowVerification={setShowVerification}
                  setShowAgeRestriction={setShowAgeRestriction}
                  promoCodeURL={promoCodeURL}
                />
              </>
            )}

            {showVerification && (
              <>
                <p className={styles.step}>{t('REGISTER_PAGE_STEP_HEADER', { step: 2 })}</p>
                <h1>{t('REGISTER_PAGE_VERIFY_HEADER')}</h1>
                <p className={styles['verification-text']}>
                  {t('REGISTER_PAGE_VERIFY_SUB_HEADER_ONE')}
                  <b>{t('REGISTER_PAGE_VERIFY_SUB_HEADER_EMAIL', { email })}</b>
                </p>
                <p>{t('REGISTER_PAGE_VERIFY_SUB_HEADER_TWO')}</p>
                <AccountVerify email={email} />
              </>
            )}

            {showAgeRestriction && (
              <div className={styles.age}>
                <Image
                  className={styles['check-mark']}
                  src="/icons/parent.svg"
                  alt="Parents icon"
                  height={64}
                  width={64}
                />
                <h1>{t('REGISTER_PAGE_UNDER_16_HEADER')}</h1>
                <p>{t('REGISTER_PAGE_UNDER_16_SUB_HEADER')}</p>
                <button className="primary full-width" onClick={() => setShowAgeRestriction(false)}>
                  {t('REGISTER_PAGE_UNDER_16_CTA')}
                </button>
              </div>
            )}
          </div>
        </div>
        <AccountImage />
      </main>
    </div>
  );
};

export const getServerSideProps = async ({ query: { PROMO_CODE }, locale = 'en', req }: GetServerSidePropsContext) => {
  try {
    const SSRAPI = withSSRContext({ req });
    await SSRAPI.Auth.currentAuthenticatedUser();

    return {
      redirect: {
        destination: 'profile',
        statusCode: 303,
      },
    };
  } catch (error: unknown) {
    return {
      props: {
        ...(await serverSideTranslations(locale, ['register', 'components'])),
        PROMO_CODE: PROMO_CODE ? PROMO_CODE : null,
      },
    };
  }
};

export default Register;
